import React from 'react';
import { createPortal } from 'react-dom';
import { X, Download } from 'lucide-react';
import { DashboardTile, DateRange } from '../../types/dashboard';
import { LineChart, BarChart, WorldMap, PieChart, HorizontalBarChart, MultiLineChart, DataTable, TriangleChart, OverlapChart, PyramidChart, MonthlyListenersChart, MonthlyFollowersChart, YouTubeMusicViewsChart, YouTubeMusicViewsMetric, EditorialPlaylistMixedChart, TotalPlaylistMixedChart, AudienceReachPyramidChart, GeographicDistributionBarChart, EngagementRatesChart, InstagramGenderDistributionChart, InstagramAgeDistributionChart, YouTubeGenderDistributionChart, BrandAffinityChart, WeeklyGrowthChart, MonthlyGrowthChart, PlatformGrowthComparisonChart, FunnelChart } from '../Charts';
import html2canvas from 'html2canvas';

interface ChartExpandModalProps {
  tile: DashboardTile;
  isOpen: boolean;
  onClose: () => void;
  dateRange?: DateRange;
  artistId?: string;
  platformArtistId?: string;
}

const ChartExpandModal: React.FC<ChartExpandModalProps> = ({ tile, isOpen, onClose, dateRange, artistId, platformArtistId }) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const chartContentRef = React.useRef<HTMLDivElement>(null);

  // Handle ESC key to close modal
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleDownloadExpandedChart = () => {
    // Try multiple ways to find the ECharts instance
    let chartContainer = chartContentRef.current?.querySelector('.echarts-for-react div');
    if (!chartContainer) {
      chartContainer = modalRef.current?.querySelector('.echarts-for-react div');
    }
    if (!chartContainer) {
      chartContainer = modalRef.current?.querySelector('[_echarts_instance_]');
    }
    
    if (chartContainer && (window as any).echarts) {
      const chartInstance = (window as any).echarts.getInstanceByDom(chartContainer);
      if (chartInstance) {
        // Get current chart size and scale it up for download
        const chartDom = chartInstance.getDom();
        const currentWidth = chartDom.offsetWidth;
        const currentHeight = chartDom.offsetHeight;
        
        // Generate and download the image as PNG at high resolution - this captures ONLY the chart
        const dataURL = chartInstance.getDataURL({
          type: 'png',
          pixelRatio: 2, // High quality (reduced from 3 for better compatibility)
          backgroundColor: '#1C1C1C', // Match modal background (ECharts uses backgroundColor)
          excludeComponents: ['toolbox'], // Exclude non-essential components
          width: Math.max(currentWidth * 1.5, 1600), // Scale up from current size
          height: Math.max(currentHeight * 1.5, 1000) // Scale up from current size
        });
        
        // Create download link
        const link = document.createElement('a');
        link.download = `${tile.title.replace(/ /g, '_').toLowerCase()}_expanded_chart.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return; // Exit early, we successfully used ECharts export
      }
    }
    
    // Fallback for non-ECharts components or if ECharts detection failed
    handleExpandedTableDownload();
  };

  const handleExpandedTableDownload = () => {
    // For tables or other non-chart components, capture only the chart content area
    const chartContentArea = modalRef.current?.querySelector('.flex-1.p-6 > div');
    if (chartContentArea) {
      html2canvas(chartContentArea as HTMLElement, {
        allowTaint: true,
        useCORS: true,
        height: (chartContentArea as HTMLElement).offsetHeight * 3,
        width: (chartContentArea as HTMLElement).offsetWidth * 3
      }).then((canvas: HTMLCanvasElement) => {
        const dataURL = canvas.toDataURL('image/png', 0.95);
        const link = document.createElement('a');
        link.download = `${tile.title.replace(/ /g, '_').toLowerCase()}_expanded_chart.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch(() => {
        alert('Download feature is not available. Please try again later.');
      });
    }
  };

  if (!isOpen) return null;

const getExpandedChart = () => {
    const commonProps = {
      className: "!bg-transparent !p-0 w-full h-full"
    };

    switch (tile.id) {
      case 'revenue':
        return (
          <LineChart
            data={[
              {
                name: 'Revenue',
                data: [12000, 19000, 15000, 22000, 24876]
              }
            ]}
            xAxisData={['Jan', 'Feb', 'Mar', 'Apr', 'May']}
            {...commonProps}
          />
        );
      case 'downloads':
        return (
          <AudienceReachPyramidChart
            artistId={artistId}
            {...commonProps}
          />
        );
      case 'geography':
        return (
          <GeographicDistributionBarChart
            artistId={artistId}
            {...commonProps}
          />
        );
      case 'platforms':
        return (
          <TotalPlaylistMixedChart
            artistId={artistId}
            platformArtistId={platformArtistId}
            {...commonProps}
          />
        );
      case 'brand-affinity':
        return (
          <BrandAffinityChart
            artistId={artistId}
            platformArtistId={platformArtistId}
            {...commonProps}
          />
        );
      case 'analytics-timeline':
        return (
          <MultiLineChart
            data={[
              { name: 'Streams', data: [2200, 2800, 2500, 3200, 2900, 3500, 4100, 3800, 4200, 4600, 4300, 4800], color: '#3B82F6' },
              { name: 'Downloads', data: [800, 1200, 1000, 1500, 1300, 1800, 2100, 1900, 2200, 2400, 2100, 2600], color: '#10B981' },
              { name: 'Revenue ($)', data: [12000, 15000, 13500, 18000, 16200, 21000, 24500, 22800, 25200, 27600, 25800, 28800], color: '#F59E0B' },
              { name: 'Active Users', data: [15000, 18500, 17200, 22000, 20500, 25000, 28500, 26800, 30200, 32500, 31000, 34000], color: '#EF4444' },
              { name: 'Engagement Rate', data: [65, 72, 68, 78, 75, 82, 88, 85, 91, 94, 89, 96], color: '#8B5CF6' }
            ]}
            xAxisData={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']}
            {...commonProps}
          />
        );
      case 'engagement':
        return (
          <PieChart
            data={[
              { name: 'Apple Music', value: 2264402, color: '#3B82F6' },
              { name: 'Soundcloud', value: 1500000, color: '#10B981' },
              { name: 'YouTube', value: 800000, color: '#EF4444' },
              { name: 'Spotify', value: 600000, color: '#F59E0B' }
            ]}
            innerRadius="40%"
            centerText=""
            {...commonProps}
          />
        );
      case 'engagement-rates':
        return (
          <EngagementRatesChart
            className="!bg-transparent !p-0 w-full h-full"
            size="full"
            artistId={artistId}
            refreshKey={tile.id === 'engagement-rates' ? tile.y : 0} // use y to change key subtly on reopen
          />
        );
      case 'instagram-gender-distribution':
        return (
          <InstagramGenderDistributionChart
            className="!bg-transparent !p-0 w-full h-full"
            size="full"
            artistId={artistId}
          />
        );
      case 'instagram-age-distribution':
        return (
          <InstagramAgeDistributionChart
            className="!bg-transparent !p-0 w-full h-full"
            size="full"
            artistId={artistId}
          />
        );
      case 'youtube-gender-distribution':
        return (
          <YouTubeGenderDistributionChart
            className="!bg-transparent !p-0 w-full h-full"
            size="full"
            artistId={artistId}
          />
        );
      case 'weekly-growth':
        return (
          <WeeklyGrowthChart
            artistId={artistId}
            {...commonProps}
          />
        );
      case 'monthly-growth':
        return (
          <MonthlyGrowthChart
            artistId={artistId}
            {...commonProps}
          />
        );
      case 'platform-growth-comparison':
        return (
          <PlatformGrowthComparisonChart
            {...commonProps}
          />
        );
      case 'spotify-funnel':
        return (
          <FunnelChart
            artistId={artistId}
            {...commonProps}
          />
        );
      case 'payouts':
        return (
          <MultiLineChart
            data={[
              { name: 'Category 01', data: [65, 70, 75, 80, 78], color: '#10B981' },
              { name: 'Category 02', data: [45, 55, 60, 65, 63], color: '#3B82F6' },
              { name: 'Category 03', data: [25, 35, 40, 45, 43], color: '#8B5CF6' }
            ]}
            xAxisData={['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']}
            {...commonProps}
          />
        );
      case 'demographics':
        return (
          <DataTable
            data={[
              { country: 'US', streams: '805,723,582', platform: 'Apple Music', growth: '+2.5%' },
              { country: 'UK', streams: '703,548,227', platform: 'Apple Music', growth: '-0.5%' },
              { country: 'CA', streams: '508,901,325', platform: 'Apple Music', growth: '+1.2%' },
              { country: 'DE', streams: '408,901,829', platform: 'Apple Music', growth: '+0.8%' }
            ]}
            columns={[
              { key: 'country', label: 'Country', width: '20%' },
              { key: 'streams', label: 'Streams', width: '35%' },
              { key: 'platform', label: 'Platform', width: '25%' },
              { 
                key: 'growth', 
                label: 'Growth', 
                width: '20%',
                render: (value: string) => (
                  <span className={value.startsWith('+') ? 'text-green-400' : 'text-red-400'}>
                    {value}
                  </span>
                )
              }
            ]}
            {...commonProps}
            maxHeight="100%"
          />
        );
      case 'triangle':
        return (
          <TriangleChart
            data={[
              { name: 'Category 01', value: 40, color: '#3B82F6' },
              { name: 'Category 02', value: 35, color: '#10B981' },
              { name: 'Category 03', value: 25, color: '#F59E0B' }
            ]}
            centerValue="100%"
            {...commonProps}
          />
        );
      case 'overlap':
        return (
          <OverlapChart
            leftCircle={{ value: 2264402, label: 'Category 01', color: '#3B82F6' }}
            rightCircle={{ value: 2264402, label: 'Category 02', color: '#10B981' }}
            overlapValue={22}
            {...commonProps}
          />
        );
      case 'monthly-listeners':
        return (
          <MonthlyListenersChart
            dateRange={dateRange}
            {...commonProps}
            className="w-full h-full"
          />
        );
      case 'monthly-followers':
        return (
          <MonthlyFollowersChart
            dateRange={dateRange}
            {...commonProps}
            className="w-full h-full"
          />
        );
      case 'youtube-views':
        return (
          <YouTubeMusicViewsMetric
            artistId={artistId}
            dateRange={dateRange}
            {...commonProps}
            className="w-full h-full"
          />
        );
      case 'editorial-playlists':
        return (
          <EditorialPlaylistMixedChart
            artistId={artistId}
            platformArtistId={platformArtistId}
            {...commonProps}
            className="w-full h-full"
          />
        );
      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-300">
            <div className="text-xl">Chart not available</div>
          </div>
        );
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div ref={modalRef} className="nova-bg-white border border-[#BBBBBB] rounded-lg w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#BBBBBB]">
          <h2 className="text-xl font-medium text-white uppercase">{tile.title}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadExpandedChart}
              className="p-2 text-gray-400 hover:text-white transition-colors border border-[#BBBBBB] rounded"
              title="Download chart"
            >
              <Download size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors border border-[#BBBBBB] rounded"
              title="Close expanded view"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Chart Content */}
        <div className="flex-1 p-6 overflow-hidden">
          <div ref={chartContentRef} className="w-full h-full">
            {getExpandedChart()}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChartExpandModal;
